import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import { getUkStoreIdM } from "../core/config/storeId";
import { activeStoreRole } from "../helpers/adminAccess";

dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);

/** Feature Matrix key of the whole WhatsApp Broadcast feature (backend: WaBroadcastService::SHARED_FEATURE). */
export const WA_BROADCAST_FEATURE = "wa_shared_number";

/** Sender connection → badge. Presentation only. */
export const ACCOUNT_STATUS = {
  connected: { tone: "success", label: "Connected" },
  linking: { tone: "accent", label: "Linking…" },
  unlinked: { tone: "muted", label: "Not linked" },
  disconnected: { tone: "danger", label: "Disconnected" },
  restricted: { tone: "danger", label: "Restricted by WhatsApp" },
};

export const accountStatus = (status) => ACCOUNT_STATUS[status] || { tone: "muted", label: status || "Unknown" };

/** One send → badge. */
export const SEND_STATUS = {
  queued: { tone: "muted", label: "Queued" },
  sending: { tone: "accent", label: "Sending" },
  sent: { tone: "accent", label: "Sent" },
  delivered: { tone: "success", label: "Delivered" },
  read: { tone: "success", label: "Read" },
  failed: { tone: "danger", label: "Failed" },
  skipped: { tone: "muted", label: "Skipped" },
  cancelled: { tone: "muted", label: "Cancelled" },
};

export const sendStatus = (status) => SEND_STATUS[status] || { tone: "muted", label: status };

/** Week-by-week ramp from the safety plan. Only move up after a clean week. */
export const RAMP_STEPS = [
  { value: 15, label: "Weeks 1–2", hint: "Pilot" },
  { value: 25, label: "Week 3" },
  { value: 40, label: "Week 4" },
  { value: 60, label: "Week 5+", hint: "Ceiling" },
  // No daily limit (Alex, 2026-10-14): the hourly cap and the gaps still apply.
  { value: null, label: "Hourly cap only", display: "No limit" },
];

export const SHARE_TYPE_OPTIONS = [
  { value: "", label: "Any" },
  { value: "listing", label: "Listings" },
  { value: "request", label: "Requests" },
];

/** 447507785188 → +44 7507 785188 */
export function formatPhone(phone) {
  if (!phone) return null;
  const digits = String(phone).replace(/\D/g, "");
  if (digits.length === 12 && digits.startsWith("44")) {
    return `+44 ${digits.slice(2, 6)} ${digits.slice(6)}`;
  }
  return `+${digits}`;
}

export const fromNow = (iso) => (iso ? dayjs(iso).fromNow() : "");

/**
 * A clock time people can act on: "Today 14:05", "Yesterday 09:12",
 * "Tomorrow 09:10", "Mon 12 Oct, 14:05" — read in `tz` (the store's own time
 * zone) when given, so "today" is the store's today, not the viewer's.
 */
export function clockTime(iso, tz = null, now = dayjs()) {
  if (!iso) return "";
  const inZone = (v) => (tz ? dayjs(v).tz(tz) : dayjs(v));
  const d = inZone(iso);
  now = inZone(now);
  const time = d.format("HH:mm");
  if (d.isSame(now, "day")) return `Today ${time}`;
  if (d.isSame(now.subtract(1, "day"), "day")) return `Yesterday ${time}`;
  if (d.isSame(now.add(1, "day"), "day")) return `Tomorrow ${time}`;
  return d.format("ddd D MMM, HH:mm");
}

/** "Europe/London" → "London", "America/New_York" → "New York". */
export const zoneName = (tz) => (tz ? tz.split("/").pop().replace(/_/g, " ") : "");

/**
 * The zone's name and offset right now, e.g. "BST, UTC+1" (London in summer),
 * "GMT, UTC+0" (London in winter), "UTC+4" (Dubai, which has no short name).
 * Follows the clock change by itself; "" for an unknown zone.
 */
export function zoneDetail(tz, now = new Date()) {
  if (!tz) return "";
  try {
    const part = (style) =>
      new Intl.DateTimeFormat("en-GB", { timeZone: tz, timeZoneName: style }).formatToParts(now).find((p) => p.type === "timeZoneName")?.value || "";
    const offset = part("shortOffset").replace(/^GMT/, "UTC");
    const utc = offset === "UTC" ? "UTC+0" : offset;
    const short = part("short");
    return short && !/^(GMT|UTC)[+-]/.test(short) ? `${short}, ${utc}` : utc;
  } catch {
    return "";
  }
}
export const atTime = (iso) => (iso ? dayjs(iso).format("ddd D MMM, HH:mm") : "");

/**
 * Rough time to finish N sends at the human pace (gaps + a break every ~10),
 * and how many fit in what is left of today's limit. Shown BEFORE sending so
 * nobody is surprised that 40 groups take most of an hour, on purpose.
 */
export function estimate(count, overview) {
  if (!count || !overview) return null;
  const [gapMin, gapMax] = overview.limits?.gap_seconds || [20, 60];
  const avgGap = (gapMin + gapMax) / 2 + 6; // + typing
  const breaks = Math.floor(count / 10) * 330;
  const minutes = Math.max(1, Math.round((count * avgGap + breaks) / 60));
  // Messages already queued go first, and outside the sending window nothing
  // goes "today" — the estimate must not promise what the sender won't do.
  const cap = overview.limits?.daily_cap;
  const left = overview.in_window === false
    ? 0
    : cap == null
      ? count // no daily limit
      : Math.max(0, cap - (overview.usage?.today || 0) - (overview.queued || 0));
  return { minutes, today: Math.min(count, left), later: Math.max(0, count - left), outsideWindow: overview.in_window === false };
}

/**
 * WhatsApp Broadcast is for master admins and the OWNERS and STORE ADMINS of
 * the active store (backend: WaBroadcastController::canUse).
 */
export function canUseWaBroadcast(user, accessibleStores, activeStoreId, hasFeature) {
  if (!user) return false;
  // The "WhatsApp Broadcast" Feature Matrix row (key kept from its days as the
  // shared-number row). Mode-aware, so nothing changes until enforce.
  if (hasFeature && !hasFeature(WA_BROADCAST_FEATURE)) return false;
  if (user.is_master_admin) return true;
  const role = activeStoreRole(user, accessibleStores, activeStoreId);
  return role === "owner" || role === "store_admin";
}

/**
 * Can this item be broadcast from the active store's numbers? Only the store's
 * own items (the backend refuses others too). A missing store is a legacy DAO
 * row; a master admin who hasn't switched store is on DAO.
 */
export function isActiveStoreItem(item, activeStoreId) {
  const dao = Number(getUkStoreIdM());
  const store = item?.store_id_m == null ? dao : Number(item.store_id_m);
  return store === Number(activeStoreId ?? dao);
}

/** File ids a listing can be shown with: its own photography first, then the catalogue shot. */
export function listingImageIds(listing) {
  return uniqueIds([
    listing?.image_id,
    ...(listing?.images || []).map((i) => i?.id ?? i),
    ...(listing?.product?.images || []).map((i) => i?.id ?? i),
  ]);
}

export function requestImageIds(request) {
  return uniqueIds([
    ...(request?.images || []).map((i) => i?.id ?? i),
    ...(request?.product?.images || []).map((i) => i?.id ?? i),
  ]);
}

function uniqueIds(ids) {
  return [...new Set(ids.filter((id) => id != null && id !== "").map(Number))].filter(Number.isFinite);
}

/**
 * One line that always says what to do next, so nobody has to work out the
 * order: link → groups → a saved list → Sending on. Disappears once all four
 * are done. Driven by the overview (polled), so it moves on by itself.
 */
export function nextStep(overview) {
  const connected = overview.account?.status === "connected";
  const groups = overview.groups?.total ?? 0;
  const lists = overview.lists;
  if (!connected) {
    return { n: 1, title: "Link your WhatsApp number", text: "Enter the number below, tick the risk statements, then type the code into WhatsApp on the phone." };
  }
  if (groups === 0) {
    return { n: 2, title: "Load your groups", text: "They load below by themselves once WhatsApp has finished opening the number. If nothing appears after a few minutes, click “Refresh from WhatsApp”." };
  }
  if (lists === 0) {
    return { n: 3, title: "Save a list of groups", text: "Under “Who it posts to”, open Saved lists → New list, name it, and tick the groups to post to. It saves as you go." };
  }
  if (overview.account.paused) {
    return { n: 4, title: "Turn Live Broadcasting on", text: "Switch “Live Broadcasting” on above. Nothing goes out until you do — then use “Share to Broadcast” on any listing." };
  }
  return null;
}

/**
 * Point every /product/<id> link at the store's own site — daowatches.com for
 * DAO's stores (overview.item_link_base); unchanged when there is no base.
 * Mirrors WaBroadcastService::siteLinks(), which applies it to what is sent.
 */
export function siteLinks(text, base) {
  if (!base || !text) return text;
  return text.replace(/https?:\/\/[^\s/]+\/product\/(\d+)/gi, `${base.replace(/\/$/, "")}/product/$1`);
}

export const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/** ISO day numbers (1 = Mon … 7 = Sun) → "Every day", "Mon–Sat", or "Mon, Wed, Fri". */
export function dayRangeLabel(days = []) {
  const sorted = [...new Set(days.map(Number))].filter((d) => d >= 1 && d <= 7).sort((a, b) => a - b);
  if (sorted.length === 7) return "Every day";
  if (sorted.length === 0) return "No days";
  const consecutive = sorted.every((d, i) => i === 0 || d === sorted[i - 1] + 1);
  if (consecutive && sorted.length > 2) return `${DAY_NAMES[sorted[0] - 1]}–${DAY_NAMES[sorted[sorted.length - 1] - 1]}`;
  return sorted.map((d) => DAY_NAMES[d - 1]).join(", ");
}

/** 9 → "09". */
export const pad = (hour) => String(hour).padStart(2, "0");

/** Drop every /product/<id> link (a store that sends listings without one). Mirrors the backend. */
export function withoutItemLinks(text) {
  if (!text) return text;
  return text
    .replace(/[ \t]*https?:\/\/[^\s/]+\/product\/\d+\S*/gi, "")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/**
 * The caption exactly as the backend sends it (WaBroadcastService::normaliseCaption):
 * legacy "\n" escapes and __SQR__ placeholders resolved, runs of 3+ line breaks
 * collapsed to one blank line, outer blank lines trimmed.
 */
export function normaliseCaption(text) {
  return (text || "")
    .replace(/\\n/g, "\n")
    .replace(/__SQR__/g, "▪️")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}